import * as React from "react";
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ShieldQuestion,
} from "lucide-react";
import {
  Badge,
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  StatCard,
  toast,
} from "@eduviaos/ui-kit";
import { MarkAttendanceSheet } from "@/components/attendance/MarkAttendanceSheet";
import { useCanManageAttendance } from "@/hooks/useAttendanceAuthoring";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useStudentsList } from "@/hooks/useStudents";
import {
  ATTENDANCE_STATUS_LABELS,
  todayIstDateKey,
  useAttendanceList,
  useRegularizeAttendance,
  type AttendanceRecord,
  type AttendanceStatus,
} from "@/lib/attendance/api";
import { getVerticalProfile } from "@/lib/verticalProfile";

function shiftDateKey(dateKey: string, deltaDays: number): string {
  const parts = dateKey.split("-").map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  const dt = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return dt.toISOString().slice(0, 10);
}

function formatMarkedAt(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusBadgeVariant(
  status: AttendanceStatus,
): "default" | "secondary" | "danger" | "outline" {
  switch (status) {
    case "present":
      return "default";
    case "late":
      return "secondary";
    case "absent":
      return "danger";
    default:
      return "outline";
  }
}

/**
 * Phase SC-8 — IA attendance workspace for study centres (and schools).
 *
 * Lists daily attendance rows from `/api/attendance`, supports manual
 * mark + regularize. SC copy comes from `verticalProfile.moduleCopy`.
 */
export function AttendancePage() {
  const { canViewAttendance, canManageAttendance, missingView } = useCanManageAttendance();
  const { vertical } = useEnabledModules();
  const profile = getVerticalProfile(vertical ?? "school");
  const personOne = profile.personNoun.one;
  const personMany = profile.personNoun.many;

  const [dateKey, setDateKey] = React.useState(todayIstDateKey);
  const [filterText, setFilterText] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "attendance",
    defaultPageSize: 25,
  });
  const [markOpen, setMarkOpen] = React.useState(false);

  const listQuery = useAttendanceList({
    date_key: dateKey,
    limit: pageSize,
    offset: page * pageSize,
  });
  const learnersQuery = useStudentsList({ status: "active", limit: 200 });
  const regularize = useRegularizeAttendance(vertical ?? undefined);

  const learnerById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const row of learnersQuery.data?.items ?? []) {
      map.set(row.id, row.name);
    }
    return map;
  }, [learnersQuery.data?.items]);

  const filteredItems = React.useMemo(() => {
    const items = listQuery.data?.items ?? [];
    const q = filterText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const name = learnerById.get(row.student_id) ?? row.student_id;
      return name.toLowerCase().includes(q) || row.status.includes(q);
    });
  }, [filterText, learnerById, listQuery.data?.items]);

  const dayStats = React.useMemo(() => {
    const items = listQuery.data?.items ?? [];
    const counts: Record<AttendanceStatus, number> = {
      present: 0,
      absent: 0,
      late: 0,
      excused: 0,
      half_day: 0,
    };
    for (const row of items) {
      if (row.status in counts) counts[row.status as AttendanceStatus] += 1;
    }
    return counts;
  }, [listQuery.data?.items]);

  const handleRegularize = async (row: AttendanceRecord) => {
    try {
      await regularize.mutateAsync({
        id: row.id,
        body: { new_status: "excused", reason: "Regularized from IA attendance workspace" },
      });
      toast.success("Attendance regularized");
    } catch {
      toast.error("Could not regularize this record");
    }
  };

  if (missingView) {
    return (
      <div className="mx-auto max-w-lg py-12">
        <div className="border-border flex items-start gap-3 rounded-lg border p-6">
          <ShieldQuestion className="text-muted-foreground mt-0.5 size-5 shrink-0" aria-hidden />
          <div>
            <h2 className="text-base font-semibold">Attendance is restricted</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Your role does not include permission to view attendance. Ask an admin to grant{" "}
              <span className="font-mono text-xs">view_attendance</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewAttendance) {
    return null;
  }

  const blurb =
    profile.moduleCopy.attendance?.blurb ??
    "Daily attendance capture, monthly heatmaps, and notifications.";

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderHeading>
            <PageHeaderTitle>Attendance</PageHeaderTitle>
            <PageHeaderDescription>{blurb}</PageHeaderDescription>
          </PageHeaderHeading>
          <PageHeaderActions>
            {canManageAttendance ? (
              <Button onClick={() => setMarkOpen(true)}>
                <Plus className="size-4" aria-hidden />
                Mark check-in
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Present" value={String(dayStats.present)} />
        <StatCard label="Late" value={String(dayStats.late)} />
        <StatCard label="Absent" value={String(dayStats.absent)} />
        <StatCard label="Excused / half day" value={String(dayStats.excused + dayStats.half_day)} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Previous day"
          onClick={() => {
            setPage(0);
            setDateKey((d) => shiftDateKey(d, -1));
          }}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-2">
          <CalendarDays className="text-muted-foreground size-4" aria-hidden />
          <Input
            type="date"
            value={dateKey}
            onChange={(e) => {
              setPage(0);
              setDateKey(e.target.value);
            }}
            className="w-[11rem]"
            aria-label="Attendance date"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Next day"
          onClick={() => {
            setPage(0);
            setDateKey((d) => shiftDateKey(d, 1));
          }}
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setPage(0);
            setDateKey(todayIstDateKey());
          }}
        >
          Today
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Refresh"
          onClick={() => void listQuery.refetch()}
        >
          <RefreshCw className="size-4" />
        </Button>
      </div>

      {listQuery.isError ? (
        <div className="border-destructive/40 bg-destructive/5 flex items-start gap-2 rounded-lg border p-4 text-sm">
          <AlertCircle className="text-destructive mt-0.5 size-4 shrink-0" aria-hidden />
          <span>Could not load attendance for {dateKey}. Try refreshing.</span>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <Input
            placeholder={`Filter ${personMany.toLowerCase()} or status…`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="max-w-xs"
            aria-label="Filter attendance rows"
          />
          <DataTableToolbarSpacer />
        </DataTableToolbar>

        <DataTableScroll>
          {listQuery.isPending ? (
            <DataTableSkeleton columns={5} rows={8} />
          ) : filteredItems.length === 0 ? (
            <DataTableEmpty
              title={`No check-ins on ${dateKey}`}
              description={
                canManageAttendance
                  ? `Mark attendance when a ${personOne.toLowerCase()} arrives, or when they scan in at the door.`
                  : `Records appear here once ${personMany.toLowerCase()} check in.`
              }
              action={
                canManageAttendance ? (
                  <Button onClick={() => setMarkOpen(true)}>Mark check-in</Button>
                ) : undefined
              }
            />
          ) : (
            <table className="w-full caption-bottom text-sm">
              <caption className="sr-only">
                Attendance for {dateKey} — {personMany}
              </caption>
              <thead className="bg-background sticky top-0 z-10 border-b">
                <tr className="divide-border/60 divide-x">
                  <th scope="col" className="h-11 px-4 text-left font-medium">
                    {personOne}
                  </th>
                  <th scope="col" className="h-11 px-4 text-left font-medium">
                    Status
                  </th>
                  <th scope="col" className="h-11 px-4 text-left font-medium">
                    Mode
                  </th>
                  <th scope="col" className="h-11 px-4 text-left font-medium">
                    Marked at
                  </th>
                  <th scope="col" className="h-11 w-[1%] px-4 text-right font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border/60 divide-y">
                {filteredItems.map((row) => (
                  <tr key={row.id} className="hover:bg-muted/40 transition-colors duration-[120ms]">
                    <td className="px-4 py-3 font-medium">
                      {learnerById.get(row.student_id) ?? row.student_id}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusBadgeVariant(row.status as AttendanceStatus)}>
                        {ATTENDANCE_STATUS_LABELS[row.status as AttendanceStatus] ?? row.status}
                      </Badge>
                      {row.regularized ? (
                        <span className="text-muted-foreground ml-2 text-xs">Regularized</span>
                      ) : null}
                    </td>
                    <td className="text-muted-foreground px-4 py-3">{row.mode_used}</td>
                    <td className="text-muted-foreground px-4 py-3 tabular-nums">
                      {formatMarkedAt(row.marked_at)}
                      {row.late_minutes > 0 ? (
                        <span className="ml-2 text-amber-600 dark:text-amber-400">
                          +{row.late_minutes}m late
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canManageAttendance && row.status === "absent" && !row.regularized ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Row actions">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => void handleRegularize(row)}>
                              Mark excused
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DataTableScroll>

        <DataTableFooter>
          <DataTablePagination
            page={page + 1}
            pageSize={pageSize}
            totalRows={listQuery.data?.total ?? 0}
            onPageChange={(next) => setPage(Math.max(0, next - 1))}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
          />
        </DataTableFooter>
      </DataTable>

      <MarkAttendanceSheet
        open={markOpen}
        onOpenChange={setMarkOpen}
        learners={learnersQuery.data?.items ?? []}
        dateKey={dateKey}
        vertical={vertical ?? undefined}
        personLabel={personOne}
      />
    </div>
  );
}
